import { z } from 'zod';

/**
 * Schema for a static response button option.
 * Follows the shared messaging button shape used by platform providers.
 */
export const responseButtonOptionSchema = z.object({
  id: z.string().trim().min(1).max(256),
  label: z.string().trim().min(1).max(20),
});

/**
 * Backwards-compatible alias for the previous schema export name.
 */
export const responseButtonDefinitionSchema = responseButtonOptionSchema;

/** Schema for supported response button generation modes. */
export const responseButtonModeSchema = z.enum(['static', 'ai', 'hybrid']);

function normalizeFallbackMessageMapping(value: Record<string, unknown>): Record<string, unknown> {
  const normalizedMapping: Record<string, unknown> = {};

  Object.entries(value).forEach(([key, mappingValue]) => {
    const normalizedKey = key.trim();
    const hasNormalizedKey = normalizedKey in normalizedMapping;
    const isExactKeyMatch = key === normalizedKey;

    if (!hasNormalizedKey || isExactKeyMatch) {
      normalizedMapping[normalizedKey] = mappingValue;
    }
  });

  return normalizedMapping;
}

const responseButtonFallbackMessageMappingSchema = z.preprocess((value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }

  return normalizeFallbackMessageMapping(value as Record<string, unknown>);
}, z.record(z.string()));

/**
 * Schema for per-surface response button behavior.
 * Supports static, AI-generated, and hybrid button strategies.
 */
export const responseButtonSurfaceConfigSchema = z.object({
  enabled: z.boolean().optional(),
  mode: responseButtonModeSchema,
  staticButtons: z.array(responseButtonOptionSchema).min(1).max(3).optional(),
  aiPromptOverride: z.string().max(4000).optional(),
  maxButtons: z.number().int().min(1).max(3).optional(),
  fallbackMessageMapping: responseButtonFallbackMessageMappingSchema.optional(),
}).passthrough().superRefine((value, ctx) => {
  if (value.staticButtons) {
    const seenIds = new Set<string>();

    value.staticButtons.forEach((button, index) => {
      if (seenIds.has(button.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['staticButtons', index, 'id'],
          message: 'staticButtons ids must be unique.',
        });
      }

      seenIds.add(button.id);
    });
  }

  if (typeof value.maxButtons === 'number' && value.staticButtons && value.staticButtons.length > value.maxButtons) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['maxButtons'],
      message: 'maxButtons cannot be smaller than the number of staticButtons.',
    });
  }
});

/**
 * Schema for bot-level response button behavior across outbound message surfaces.
 */
export const responseButtonConfigSchema = z.object({
  enabled: z.boolean(),
  dailyCheckIn: responseButtonSurfaceConfigSchema.optional(),
  weeklySummary: responseButtonSurfaceConfigSchema.optional(),
  chatResponses: responseButtonSurfaceConfigSchema.optional(),
}).passthrough().superRefine((value, ctx) => {
  if (!value.enabled) {
    return;
  }

  const surfaces = [
    ['dailyCheckIn', value.dailyCheckIn],
    ['weeklySummary', value.weeklySummary],
    ['chatResponses', value.chatResponses],
  ] as const;
  const hasEnabledSurface = surfaces.some(([, surfaceConfig]) => (
    surfaceConfig !== undefined && surfaceConfig.enabled !== false
  ));

  if (!hasEnabledSurface) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['enabled'],
      message: 'At least one surface must be enabled when response buttons are enabled.',
    });
  }

  for (const [surfaceName, surfaceConfig] of surfaces) {
    if (!surfaceConfig) {
      continue;
    }

    if ((surfaceConfig.mode === 'static' || surfaceConfig.mode === 'hybrid') && !surfaceConfig.staticButtons?.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [surfaceName, 'staticButtons'],
        message: 'staticButtons are required when mode is "static" or "hybrid".',
      });
      continue;
    }

    if (
      (surfaceConfig.mode === 'static' || surfaceConfig.mode === 'hybrid')
      && surfaceConfig.staticButtons
      && surfaceConfig.fallbackMessageMapping
    ) {
      const buttonIds = new Set(surfaceConfig.staticButtons.map((button) => button.id));

      for (const key of Object.keys(surfaceConfig.fallbackMessageMapping)) {
        if (!buttonIds.has(key)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [surfaceName, 'fallbackMessageMapping', key],
            message: 'fallbackMessageMapping keys must match staticButtons ids.',
          });
        }
      }

      for (const button of surfaceConfig.staticButtons) {
        if (!(button.id in surfaceConfig.fallbackMessageMapping)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [surfaceName, 'fallbackMessageMapping', button.id],
            message: 'fallbackMessageMapping must include every staticButtons id when provided.',
          });
        }
      }
    }
  }
});
